import { useCallback, useState } from 'react';
import { ReservationTimeDisplay } from 'types/reservationDisplaySettings.type';
import { PublishingConfigurationModal } from '../components/PublishingConfig';

export interface UsePublishingConfigDialogParams {
  // Note: index can be undefined for single (non-array) items like seat/location
  onArrayFieldChange: (
    dataPath: string
  ) => (index: number | undefined, key: string, value: any) => void;
}

interface ModalState {
  open: boolean;
  title: string;
  path: string; // seat | location | fields | information
  index?: number; // only for array items
  record: { enabled: boolean; timeDisplay?: ReservationTimeDisplay };
}

export function usePublishingConfigDialog({
  onArrayFieldChange,
}: UsePublishingConfigDialogParams) {
  const [state, setState] = useState<ModalState | null>(null);

  const openDialog = useCallback((params: Omit<ModalState, 'open'>) => {
    setState({ ...params, open: true });
  }, []);

  const closeDialog = useCallback(() => {
    setState((prev) => (prev ? { ...prev, open: false } : prev));
  }, []);

  const openSingle = useCallback(
    (
      path: string,
      title: string,
      record: { enabled: boolean; timeDisplay?: ReservationTimeDisplay }
    ) => {
      openDialog({ path, title, record });
    },
    [openDialog]
  );

  const openIndexed = useCallback(
    (
      basePath: string,
      index: number,
      title: string,
      record: { enabled: boolean; timeDisplay?: ReservationTimeDisplay }
    ) => {
      openDialog({ path: basePath, index, title, record });
    },
    [openDialog]
  );

  const modalEl = state ? (
    <PublishingConfigurationModal
      title={state.title}
      isOpen={state.open}
      record={state.record}
      index={state.index}
      path={state.path}
      onArrayFieldChange={onArrayFieldChange}
      onCancel={() => closeDialog()}
    />
  ) : null;

  return { openSingle, openIndexed, modalEl };
}
